import axios from 'axios'
import moment from 'moment'
import 'moment/locale/es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { constants, HOURS_BEFORE_OPEN } from '../Constants'
import { AlertDialogAbandonClass } from '../components/AlertDialogs/AlertDialogAbandonClass'
import { ModalChooseAvatar } from '../components/Modals/ModalChooseAvatar'
import { ModalForceChangePassword } from '../components/Modals/ModalForceChangePassword'
import { Badge, Button, Modal, PageLoader } from '../components/ui'
import { useToast } from '../components/ui/Toast/Toast'
import './HomeView.scss'

moment.locale('es')

const useNextClassCountdown = days => {
  const [countdown, setCountdown] = useState(null)
  const [nextClass, setNextClass] = useState(null)
  const intervalRef = useRef(null)

  useEffect(() => {
    // Buscar próxima clase inscrita; si no hay, la primera disponible futura
    const allEvents = days.flatMap(d => d.events)
    const now = moment()
    const futureEnrolled = allEvents
      .filter(e => e.isParticipant && moment(e.start).isAfter(now))
      .sort((a, b) => moment(a.start).diff(moment(b.start)))

    const target = futureEnrolled[0] || null
    setNextClass(target)

    if (!target) {
      setCountdown(null)
      return
    }

    const tick = () => {
      const targetTime = moment.utc(target.start)
      const diff = targetTime.diff(moment().utcOffset(0, true))
      if (diff <= 0) {
        setCountdown(null)
        clearInterval(intervalRef.current)
        return
      }
      const d = moment.duration(diff)
      setCountdown({
        days: Math.floor(d.asDays()),
        hours: d.hours(),
        minutes: d.minutes(),
        seconds: d.seconds(),
      })
    }

    tick()
    intervalRef.current = setInterval(tick, 1000)
    return () => clearInterval(intervalRef.current)
  }, [days])

  return { countdown, nextClass }
}

const getMotivation = (weekCount, totalEnrolled) => {
  if (weekCount === 0)
    return {
      emoji: '😴',
      title: 'Esta semana aún no te has movido',
      sub: 'Inscríbete a una clase y empieza la racha',
      level: 0,
    }
  if (weekCount === 1)
    return {
      emoji: '🔥',
      title: '¡Primer paso dado!',
      sub: 'Una clase es mejor que ninguna. ¡Ven, entrena y disfruta!',
      level: 1,
    }
  if (weekCount === 2)
    return {
      emoji: '💪🏽',
      title: 'Cogiendo ritmo',
      sub: `${weekCount} clases esta semana. ¡Vas bien!`,
      level: 2,
    }
  if (weekCount === 3)
    return {
      emoji: '⚡',
      title: '¡Semana sólida!',
      sub: `${weekCount} clases. El hábito ya está formándose.`,
      level: 3,
    }
  if (weekCount === 4)
    return {
      emoji: '🏆',
      title: '¡Máquina!',
      sub: `${weekCount} clases esta semana. Eres una bestia.`,
      level: 4,
    }
  if (weekCount >= 5)
    return {
      emoji: '👑',
      title: '¡LEYENDA!',
      sub: `${weekCount} clases esta semana. Nivel sobrehumano. 🔥`,
      level: 5,
    }
}

const HomeView = ({ user }) => {
  const localUser = JSON.parse(localStorage.getItem('user'))
  const [days, setDays] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingPayments, setLoadingPayments] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState(
    () => localUser?.mustChangePassword === true
  )
  const [needsAvatar, setNeedsAvatar] = useState(() => !localUser?.avatarType)
  const toast = useToast()

  const isLastDayOfMonth = moment().isSame(moment().endOf('month'), 'day')

  const fetchEvents = useCallback(async () => {
    try {
      // Empezar desde el lunes de la semana actual para que las clases
      // ya pasadas de esta semana se incluyan en el conteo motivacional
      const from = moment().startOf('isoWeek').format('YYYY-MM-DD')
      const to = moment().add(30, 'days').format('YYYY-MM-DD')
      const userId = localUser?.id || ''
      const response = await axios.get(
        `${constants.urlLocal}events/grouped/${userId}?from=${from}&to=${to}`
      )
      if (response.data.ok) {
        setDays(response.data.days)
      }
    } catch (error) {
      console.error('Error al obtener eventos:', error)
    } finally {
      setLoading(false)
    }
  }, [localUser?.id])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const handleRefresh = async () => {
    setLoading(true)
    await fetchEvents()
  }

  const handleRefreshPayments = async () => {
    setLoadingPayments(true)
    try {
      await axios.get(`${constants.urlLocal}api/cron/payments`)
      toast({
        title: '✅ Pagos actualizados',
        description:
          'El estado de suscripción de todos los usuarios ha sido verificado.',
        status: 'success',
      })
    } catch (error) {
      toast({
        title: 'Error al refrescar pagos',
        description: error.response?.data?.message || 'Inténtalo de nuevo.',
        status: 'error',
      })
    } finally {
      setLoadingPayments(false)
    }
  }

  const { countdown, nextClass } = useNextClassCountdown(days)

  if (loading) return <PageLoader text="Cargando clases..." />

  return (
    <div className="home-view page">
      {/* Modal obligatoria de cambio de contraseña */}
      {mustChangePassword && (
        <ModalForceChangePassword
          user={localUser}
          onPasswordChanged={() => setMustChangePassword(false)}
        />
      )}

      {/* Modal para elegir avatar (solo si no necesita cambiar contraseña) */}
      {!mustChangePassword && needsAvatar && (
        <ModalChooseAvatar
          user={localUser}
          onAvatarChosen={() => setNeedsAvatar(false)}
        />
      )}

      {/* Welcome Section */}
      <div className="home-view__welcome">
        <div className="home-view__welcome-text">
          <h1>¡Hola, {localUser?.name || 'Deportista'} 👋🏽!</h1>
          <p>
            {(() => {
              const thisWeek = days
                .flatMap(d => d.events)
                .filter(e =>
                  moment(e.start).isBetween(
                    moment().startOf('isoWeek'),
                    moment().endOf('isoWeek'),
                    null,
                    '[]'
                  )
                ).length
              return thisWeek > 0
                ? `${thisWeek} clase${thisWeek > 1 ? 's' : ''} esta semana`
                : 'Sin clases esta semana'
            })()}
          </p>
        </div>
        <button
          className="home-view__refresh"
          onClick={handleRefresh}
          title="Actualizar"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      </div>

      {user && !user.hasPaid && (
        <div className="home-view__alert">
          <span>🚨</span>
          <p>
            Tu suscripción ha expirado. Renuévala en el BOX para poder
            inscribirte.
          </p>
        </div>
      )}

      {new Date() < new Date('2026-07-27T23:59:59') && (
        <div className="home-view__champion">
          <div className="home-view__champion-sparkles" aria-hidden="true">
            <span className="home-view__champion-sparkle" />
            <span className="home-view__champion-sparkle" />
            <span className="home-view__champion-sparkle" />
            <span className="home-view__champion-sparkle" />
            <span className="home-view__champion-sparkle" />
            <span className="home-view__champion-sparkle" />
          </div>
          <div className="home-view__champion-logo-wrap">
            <img
              src="/espana_2_estrellas_0.png"
              alt="España Campeona del Mundo"
              className="home-view__champion-logo"
              width="120"
              height="120"
            />
          </div>
          <div className="home-view__champion-text">
            <strong>Somos campeones del mundo</strong>
            <span>¡Viva España! 🏆</span>
          </div>
        </div>
      )}

      {localUser?.role === 'admin' && isLastDayOfMonth && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '1px solid #e2b04a',
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            marginBottom: '1.25rem',
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>💳</span>
          <div style={{ flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontWeight: 700,
                color: '#e2b04a',
                fontSize: '0.9rem',
              }}
            >
              Fin de mes
            </p>
            <p style={{ margin: 0, color: '#aaa', fontSize: '0.8rem' }}>
              Verifica el estado de suscripción de todos los usuarios
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleRefreshPayments}
            disabled={loadingPayments}
          >
            {loadingPayments ? 'Verificando...' : '🔄 Refrescar pagos'}
          </Button>
        </div>
      )}

      {/* Countdown próxima clase */}
      {countdown && nextClass && (
        <div className="home-view__countdown">
          <div className="home-view__countdown-label">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Próxima clase · <strong>{nextClass.title}</strong> ·{' '}
            {moment(nextClass.start).utc().format('HH:mm')}
          </div>
          <div className="home-view__countdown-timer">
            {countdown.days > 0 && (
              <div className="home-view__countdown-unit">
                <span className="home-view__countdown-num">
                  {countdown.days}
                </span>
                <span className="home-view__countdown-seg">d</span>
              </div>
            )}
            <div className="home-view__countdown-unit">
              <span className="home-view__countdown-num">
                {String(countdown.hours).padStart(2, '0')}
              </span>
              <span className="home-view__countdown-seg">h</span>
            </div>
            <div className="home-view__countdown-sep">:</div>
            <div className="home-view__countdown-unit">
              <span className="home-view__countdown-num">
                {String(countdown.minutes).padStart(2, '0')}
              </span>
              <span className="home-view__countdown-seg">m</span>
            </div>
            <div className="home-view__countdown-sep">:</div>
            <div className="home-view__countdown-unit">
              <span className="home-view__countdown-num">
                {String(countdown.seconds).padStart(2, '0')}
              </span>
              <span className="home-view__countdown-seg">s</span>
            </div>
          </div>
        </div>
      )}

      {/* Motivation card */}
      {(() => {
        const now = moment()
        const startOfWeek = now.clone().startOf('isoWeek')
        const endOfWeek = now.clone().endOf('isoWeek')
        const allEvents = days.flatMap(d => d.events)
        const weekEnrolled = allEvents.filter(
          e =>
            e.isParticipant &&
            moment(e.start).isBetween(startOfWeek, endOfWeek, null, '[]')
        ).length
        const totalEnrolled = allEvents.filter(e => e.isParticipant).length
        const mot = getMotivation(weekEnrolled, totalEnrolled)
        const bars = [0, 1, 2, 3, 4]
        return (
          <div
            className={`home-view__motivation home-view__motivation--level${mot.level}`}
          >
            <span className="home-view__motivation-emoji">{mot.emoji}</span>
            <div className="home-view__motivation-text">
              <strong>{mot.title}</strong>
              <span>{mot.sub}</span>
            </div>
            <div className="home-view__motivation-bars">
              {bars.map(i => (
                <div
                  key={i}
                  className={`home-view__motivation-bar ${i < mot.level ? 'home-view__motivation-bar--on' : ''}`}
                />
              ))}
            </div>
          </div>
        )
      })()}

      {/* Days List */}
      <div className="home-view__days">
        {(() => {
          // Mostrar solo días desde hoy; los días pasados de la semana
          // se usan únicamente para el conteo motivacional
          const visibleDays = days.filter(d =>
            moment(d.date).isSameOrAfter(moment(), 'day')
          )
          return visibleDays.length === 0 ? (
            <div className="home-view__empty">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <h3>No hay clases programadas</h3>
              <p>Las nuevas clases aparecerán aquí</p>
            </div>
          ) : (
            visibleDays.map(day => (
              <DayGroup
                key={day.date}
                day={day}
                onAction={fetchEvents}
                localUser={localUser}
              />
            ))
          )
        })()}
      </div>
    </div>
  )
}

const DayGroup = ({ day, onAction, localUser }) => {
  return (
    <div className={`day-group ${day.isToday ? 'day-group--today' : ''}`}>
      <div className="day-group__header">
        <div className="day-group__date">
          <span className="day-group__number">{day.dayNumber}</span>
          <div className="day-group__meta">
            <span className="day-group__name">{day.dayName}</span>
            <span className="day-group__month">{day.month}</span>
          </div>
        </div>
        {day.isToday && (
          <Badge variant="primary" size="sm">
            Hoy
          </Badge>
        )}
        <span className="day-group__count">
          {day.events.length} {day.events.length === 1 ? 'clase' : 'clases'}
        </span>
      </div>
      <div className="day-group__events">
        {day.events.map(event => (
          <EventCard
            key={event._id}
            event={event}
            onAction={onAction}
            localUser={localUser}
          />
        ))}
      </div>
    </div>
  )
}

const EventCard = ({ event, onAction, localUser }) => {
  const toast = useToast()
  const startTime = moment(event.start).utc().format('HH:mm')
  const endTime = moment(event.end).utc().format('HH:mm')
  const fillPercent = Math.min(
    (event.inscriptionCount / event.maxInscriptions) * 100,
    100
  )
  const [actionLoading, setActionLoading] = useState(null)
  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const isAdmin = localUser?.role === 'admin'
  const eventHasPassed = moment().isAfter(moment(event.end).subtract(2, 'hour'))

  const handleDeleteEvent = async () => {
    setDeleting(true)
    try {
      await axios.delete(`${constants.urlLocal}events/delete/${event._id}`)
      toast({ title: 'Clase eliminada', status: 'success' })
      setDeleteOpen(false)
      onAction()
    } catch (error) {
      toast({
        title: 'Error al eliminar la clase',
        description: error.response?.data?.message || 'Inténtalo de nuevo',
        status: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  // tooEarly reactivo — se recalcula cada minuto
  const [tooEarly, setTooEarly] = useState(() =>
    moment().add(HOURS_BEFORE_OPEN, 'hours').isBefore(moment(event.start))
  )
  useEffect(() => {
    const check = () =>
      setTooEarly(
        moment().add(HOURS_BEFORE_OPEN, 'hours').isBefore(moment(event.start))
      )
    const interval = setInterval(check, 60_000)
    return () => clearInterval(interval)
  }, [event.start])

  // Contador de bloqueo — solo horas y minutos
  const [lockTime, setLockTime] = useState('')
  const lockRef = useRef(null)
  useEffect(() => {
    if (!tooEarly) {
      setLockTime('')
      return
    }
    const openAt = moment(event.start).subtract(HOURS_BEFORE_OPEN, 'hours')
    const tick = () => {
      const diff = openAt.diff(moment())
      if (diff <= 0) {
        setLockTime('')
        clearInterval(lockRef.current)
        return
      }
      const d = moment.duration(diff)
      const h = Math.floor(d.asHours())
      const m = d.minutes()
      setLockTime(`${h}h ${String(m).padStart(2, '0')}m`)
    }
    tick()
    lockRef.current = setInterval(tick, 60_000)
    return () => clearInterval(lockRef.current)
  }, [event.start, tooEarly])

  // Color de la barra: progresión suave verde → amarillo → rojo según % de ocupación
  // 0% = hsl(142, 72%, 45%)  verde
  // 50% = hsl(38, 92%, 50%)  naranja
  // 100% = hsl(0, 84%, 55%)  rojo
  const barColor = (() => {
    const p = fillPercent / 100
    let h, s, l
    if (p <= 0.5) {
      // verde (142°) → naranja (38°)
      const t = p / 0.5
      h = Math.round(142 + (38 - 142) * t)
      s = Math.round(72 + (92 - 72) * t)
      l = Math.round(45 + (50 - 45) * t)
    } else {
      // naranja (38°) → rojo (0°)
      const t = (p - 0.5) / 0.5
      h = Math.round(38 + (0 - 38) * t)
      s = Math.round(92 + (84 - 92) * t)
      l = Math.round(50 + (55 - 50) * t)
    }
    return `hsl(${h}, ${s}%, ${l}%)`
  })()

  const handleInscribe = async e => {
    e.preventDefault()
    e.stopPropagation()
    setActionLoading('inscribe')
    try {
      await axios.post(`${constants.urlLocal}inscription/create`, {
        userId: localUser.id,
        eventId: event._id,
      })
      toast({
        title: '¡Inscrito! 💪🏽',
        description: 'Ven, entrena y disfruta',
        status: 'success',
      })
      onAction()
    } catch (error) {
      toast({
        title: 'Error al inscribirse',
        description: error.response?.data?.message,
        status: 'error',
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleUnsubscribe = async e => {
    e.preventDefault()
    e.stopPropagation()

    // Verificar si falta menos de 1 hora para que COMIENCE la clase
    const minutesUntilStart = moment(event.start).diff(moment(), 'minutes') - 50

    if (Math.abs(minutesUntilStart) < 60 && minutesUntilStart !== 0) {
      // Si falta menos de 1h para que inicie, mostrar dialog
      setAbandonDialogOpen(true)
      return
    }

    // Si falta más de 1h, abandonar directamente
    await performUnsubscribe()
  }

  const performUnsubscribe = async () => {
    setActionLoading('unsubscribe')
    try {
      await axios.delete(
        `${constants.urlLocal}inscription/delete/${localUser.id}/${event._id}`
      )
      toast({ title: 'Has abandonado la clase', status: 'warning' })
      setAbandonDialogOpen(false)
      onAction()
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message,
        status: 'error',
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleWaitlist = async e => {
    e.preventDefault()
    e.stopPropagation()
    setActionLoading('waitlist')
    try {
      if (event.isWaitlist) {
        await axios.delete(
          `${constants.urlLocal}waitlist/user/${localUser.id}/event/${event._id}`
        )
        toast({ title: 'Saliste de la lista de espera', status: 'info' })
      } else {
        await axios.post(`${constants.urlLocal}waitlist/create`, {
          userId: localUser.id,
          eventId: event._id,
        })
        toast({ title: 'En lista de espera', status: 'success' })
      }
      onAction()
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message,
        status: 'error',
      })
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div className="event-card">
      <Link to={`/event/${event._id}`} className="event-card__main">
        <div
          className="event-card__color"
          style={{ backgroundColor: event.categoryColor || event.color }}
        />
        <div className="event-card__content">
          <div className="event-card__top">
            <div className="event-card__info">
              <h4 className="event-card__title">{event.title}</h4>
              <p className="event-card__time">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {startTime} - {endTime}
              </p>
            </div>
            <div className="event-card__badges">
              {event.isParticipant && (
                <Badge variant="success" size="sm">
                  ✓ Inscrit@
                </Badge>
              )}
            </div>
          </div>
          <div className="event-card__bottom">
            <div className="event-card__capacity">
              <div className="event-card__bar">
                <div
                  className="event-card__fill"
                  style={{ width: `${fillPercent}%`, background: barColor }}
                />
              </div>
              <span
                className={`event-card__spots ${event.isFull ? 'event-card__spots--full' : ''}`}
                style={!event.isFull ? { color: barColor } : undefined}
              >
                {event.isFull
                  ? 'Completa'
                  : `${event.inscriptionCount}/${event.maxInscriptions}`}
              </span>
            </div>
            <svg
              className="event-card__arrow"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </div>
      </Link>

      {/* Action buttons */}
      {(!eventHasPassed || isAdmin) && (
        <div className="event-card__actions">
          {!eventHasPassed &&
            (event.isParticipant ? (
              <button
                className="event-card__action-btn event-card__action-btn--leave"
                onClick={handleUnsubscribe}
                disabled={!!actionLoading}
                title="Abandonar clase"
              >
                {actionLoading === 'unsubscribe' ? (
                  <span className="event-card__action-spinner" />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                )}
                <span>Abandonar</span>
              </button>
            ) : event.isFull ? (
              <button
                className={`event-card__action-btn ${event.isWaitlist ? 'event-card__action-btn--waitlist-leave' : 'event-card__action-btn--waitlist'}`}
                onClick={handleWaitlist}
                disabled={!!actionLoading}
                title={
                  event.isWaitlist
                    ? 'Salir de la cola'
                    : 'Apuntarse a la espera'
                }
              >
                {actionLoading === 'waitlist' ? (
                  <span className="event-card__action-spinner" />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" />
                    <line x1="3" y1="12" x2="3.01" y2="12" />
                    <line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                )}
                <span>
                  {event.isWaitlist ? 'Salir cola' : 'Entrar lista de espera'}
                </span>
              </button>
            ) : tooEarly ? (
              <div
                className="event-card__action-btn event-card__action-btn--locked"
                title={`Inscripciones abiertas ${HOURS_BEFORE_OPEN}h antes`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span className="event-card__lock-time">{lockTime}</span>
              </div>
            ) : (
              <button
                className="event-card__action-btn event-card__action-btn--join"
                onClick={handleInscribe}
                disabled={!!actionLoading}
                title="Inscribirse"
              >
                {actionLoading === 'inscribe' ? (
                  <span className="event-card__action-spinner" />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <line x1="20" y1="8" x2="20" y2="14" />
                    <line x1="23" y1="11" x2="17" y2="11" />
                  </svg>
                )}
                <span>Inscribirse</span>
              </button>
            ))}
          {isAdmin && (
            <button
              className="event-card__action-btn event-card__action-btn--leave"
              onClick={() => setDeleteOpen(true)}
              disabled={deleting}
              title="Eliminar clase"
              aria-label="Eliminar clase"
            >
              {deleting ? (
                <span className="event-card__action-spinner" />
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              )}
              <span>Eliminar</span>
            </button>
          )}
        </div>
      )}

      {/* Confirmación de borrado (admin) */}
      <Modal
        isOpen={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        title="Eliminar clase"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteEvent}
              loading={deleting}
            >
              Eliminar
            </Button>
          </>
        }
      >
        <p>
          ¿Seguro que quieres eliminar <strong>{event.title}</strong> del{' '}
          {moment(event.start).utc().format('DD/MM [a las] HH:mm')}? Esta acción
          no se puede deshacer.
        </p>
        {event.inscriptionCount > 0 && (
          <p
            style={{
              marginTop: '0.75rem',
              color: '#c0392b',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            ⚠️ Esta clase tiene {event.inscriptionCount} inscrit@
            {event.inscriptionCount > 1 ? 's' : ''}.
          </p>
        )}
      </Modal>

      {/* Dialog para ceder puesto si abandona menos de 1h antes */}
      <AlertDialogAbandonClass
        isOpen={abandonDialogOpen}
        onClose={() => setAbandonDialogOpen(false)}
        onConfirm={performUnsubscribe}
        eventTitle={event.title}
        eventTime={startTime}
      />
    </div>
  )
}

export default HomeView
